'use client';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, Scale, Copy, Check, ArrowRight, Loader2, Shield, FileText, BarChart3, Clock } from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import AppImage from '@/components/ui/AppImage';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';

interface LoginForm {
  email: string;
  password: string;
  remember: boolean;
  fullName?: string;
}

// Demo credentials for quick access
const DEMO_CREDENTIALS = { email: 'dr.hoffmann@juraproof.de', password: 'Gutachten2026!' };

const features = [
  { id: 'feat-akten', icon: FileText, text: 'Alle Akten & Fristen auf einen Blick' },
  { id: 'feat-editor', icon: Scale, text: 'Strukturierter Gutachten-Editor mit Vorlagen' },
  { id: 'feat-analytics', icon: BarChart3, text: 'Praxisanalyse & Abrechnungsübersicht' },
  { id: 'feat-security', icon: Shield, text: 'DSGVO-konform, Ende-zu-Ende verschlüsselt' },
];

export default function LoginContent() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<'email' | 'password' | null>(null);
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [isDemoSelected, setIsDemoSelected] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const { signIn, signUp } = useAuth();

  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<LoginForm>();

  const watchedEmail = watch('email');

  // Detect if demo credentials are entered
  useEffect(() => {
    setIsDemoSelected(watchedEmail === DEMO_CREDENTIALS.email);
  }, [watchedEmail]);

  // Show session expired message if redirected from demo timeout
  useEffect(() => {
    if (searchParams?.get('demo_timeout') === '1') {
      toast.warning('Ihre Demo-Sitzung ist nach 60 Minuten automatisch abgelaufen.');
    }
  }, [searchParams]);

  const copyToClipboard = (field: 'email' | 'password') => {
    const val = field === 'email' ? DEMO_CREDENTIALS.email : DEMO_CREDENTIALS.password;
    navigator.clipboard.writeText(val);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const autofill = () => {
    setValue('email', DEMO_CREDENTIALS.email);
    setValue('password', DEMO_CREDENTIALS.password);
    setIsDemoSelected(true);
    toast.info('Demo-Zugangsdaten eingefügt');
  };

  const onSubmit = async (data: LoginForm) => {
    setLoading(true);
    try {
      if (mode === 'login') {
        await signIn(data.email, data.password);
        if (data.email === DEMO_CREDENTIALS.email) {
          toast.success('Demo-Zugang aktiv — automatischer Logout nach 60 Minuten');
        } else {
          toast.success('Erfolgreich angemeldet');
        }
        router.push('/cases-dashboard');
      } else {
        await signUp(data.email, data.password, { fullName: data.fullName || '' });
        toast.success('Konto erstellt — 3-tägige Testphase gestartet. Bitte bestätigen Sie Ihre E-Mail-Adresse.');
      }
    } catch (err: any) {
      const msg = err?.message || 'Anmeldung fehlgeschlagen';
      if (msg.includes('Invalid login credentials') || msg.includes('invalid_credentials')) {
        toast.error('Ungültige Zugangsdaten — bitte überprüfen Sie E-Mail und Passwort');
      } else if (msg.includes('Email not confirmed')) {
        toast.error('E-Mail-Adresse noch nicht bestätigt — bitte prüfen Sie Ihr Postfach');
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] bg-primary flex-col justify-between p-12 relative overflow-hidden" aria-hidden="true">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 rounded-full border-2 border-white" />
          <div className="absolute top-40 left-40 w-96 h-96 rounded-full border border-white" />
          <div className="absolute bottom-20 right-10 w-48 h-48 rounded-full border-2 border-white" />
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-16">
            <AppImage
              src="/assets/images/ChatGPT_Image_5._Apr._2026_22_45_42-1775422610576.png"
              alt="JuraProof Logo"
              width={44}
              height={44}
              className="object-contain rounded-xl"
              priority={true}
            />
            <span className="text-white text-xl font-bold tracking-tight">juraproof</span>
          </div>

          <h1 className="text-4xl font-bold text-white leading-tight mb-4">
            Gutachten-Software<br />für Sachverständige
          </h1>
          <p className="text-white/70 text-lg leading-relaxed mb-12 max-w-md">
            Verwalten Sie Ihre Akten, erstellen Sie rechtssichere Gutachten und behalten Sie Fristen und Abrechnung im Blick — in einer professionellen Plattform.
          </p>

          <ul className="space-y-4" aria-label="Funktionsübersicht">
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <li key={f.id} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0" aria-hidden="true">
                    <Icon size={15} className="text-white" />
                  </div>
                  <span className="text-white/85 text-sm">{f.text}</span>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="relative z-10">
          <p className="text-white/50 text-xs">© 2026 juraproof GmbH · Alle Rechte vorbehalten</p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={36} />
            <span className="text-xl font-bold text-primary">juraproof</span>
          </div>

          <header className="mb-8">
            <h2 className="text-2xl font-bold text-foreground">
              {mode === 'login' ? 'Willkommen zurück' : 'Konto erstellen'}
            </h2>
            <p className="text-muted-foreground text-sm mt-1">
              {mode === 'login' ?'Melden Sie sich an, um Ihre Akten aufzurufen' :'3 Tage kostenlos testen — danach Jahreslizenz (299 € einmalig + 99 €/Jahr ab Jahr 2)'}
            </p>
          </header>

          {/* Mode toggle */}
          <div className="flex bg-muted rounded-lg p-1 mb-6" role="tablist" aria-label="Anmeldemodus wählen">
            {(['login', 'signup'] as const).map((m) => (
              <button
                key={`mode-${m}`}
                role="tab"
                aria-selected={mode === m}
                onClick={() => setMode(m)}
                className={`flex-1 py-2 rounded-md text-sm font-semibold transition-all duration-150 ${
                  mode === m ? 'bg-card shadow-card text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {m === 'login' ? 'Anmelden' : 'Registrieren'}
              </button>
            ))}
          </div>

          {/* Demo session warning */}
          {mode === 'login' && isDemoSelected && (
            <div className="mb-4 flex items-start gap-2.5 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <Clock size={15} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-700">
                <strong>Demo-Zugang:</strong> Automatischer Logout nach 60 Minuten.
              </p>
            </div>
          )}

          {/* Trial info for signup */}
          {mode === 'signup' && (
            <div className="mb-4 flex items-start gap-2.5 p-3 rounded-lg bg-green-50 border border-green-200">
              <Shield size={15} className="text-green-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-green-700">
                <strong>3 Tage kostenlos testen.</strong> Nach Ablauf wird automatisch eine Jahreslizenz aktiviert (299 € + 99 €/Jahr ab Jahr 2), sofern Sie nicht kündigen.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate aria-label={mode === 'login' ? 'Anmeldeformular' : 'Registrierungsformular'}>
            {mode === 'signup' && (
              <div>
                <label htmlFor="signup-name" className="label">Vollständiger Name</label>
                <input
                  id="signup-name"
                  className="input"
                  placeholder="Dr. Max Mustermann"
                  type="text"
                  autoComplete="name"
                  aria-label="Vollständiger Name"
                  {...register('fullName')}
                />
              </div>
            )}

            <div>
              <label htmlFor="login-email" className="label">E-Mail-Adresse</label>
              <input
                id="login-email"
                type="email"
                className={`input ${errors.email ? 'border-danger' : ''}`}
                placeholder="gutachter@kanzlei.de"
                autoComplete="email"
                aria-required="true"
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? 'email-error' : undefined}
                {...register('email', {
                  required: 'E-Mail ist erforderlich',
                  pattern: { value: /^\S+@\S+\.\S+$/, message: 'Ungültige E-Mail-Adresse' },
                })}
              />
              {errors.email && <p id="email-error" role="alert" className="text-xs text-danger mt-1">{errors.email.message}</p>}
            </div>

            <div>
              <label htmlFor="login-password" className="label">Passwort</label>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  className={`input pr-10 ${errors.password ? 'border-danger' : ''}`}
                  placeholder="Mindestens 8 Zeichen"
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  aria-required="true"
                  aria-invalid={!!errors.password}
                  aria-describedby={errors.password ? 'password-error' : undefined}
                  {...register('password', {
                    required: 'Passwort ist erforderlich',
                    minLength: { value: 8, message: 'Mindestens 8 Zeichen erforderlich' },
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
                </button>
              </div>
              {errors.password && <p id="password-error" role="alert" className="text-xs text-danger mt-1">{errors.password.message}</p>}
            </div>

            {mode === 'login' && (
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" {...register('remember')} className="rounded" aria-label="Angemeldet bleiben" />
                  <span className="text-xs text-muted-foreground">Angemeldet bleiben</span>
                </label>
                <button type="button" className="text-xs text-primary hover:underline" aria-label="Passwort zurücksetzen">Passwort vergessen?</button>
              </div>
            )}

            {mode === 'signup' && (
              <p className="text-xs text-muted-foreground">
                Mit der Registrierung stimmen Sie unseren{' '}
                <span className="text-primary cursor-pointer hover:underline" role="link" tabIndex={0}>Nutzungsbedingungen</span>{' '}
                und der{' '}
                <span className="text-primary cursor-pointer hover:underline" role="link" tabIndex={0}>Datenschutzerklärung</span>{' '}
                zu. Nach dem 3-tägigen Testzeitraum wird automatisch eine Jahreslizenz aktiviert, sofern keine Kündigung erfolgt.
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full justify-center py-2.5 text-sm mt-2"
              aria-busy={loading}
              aria-label={loading ? 'Wird angemeldet…' : mode === 'login' ? 'Anmelden' : 'Konto erstellen'}
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                  Wird angemeldet…
                </>
              ) : (
                <>
                  {mode === 'login' ? 'Anmelden' : 'Konto erstellen & Testphase starten'}
                  <ArrowRight size={16} aria-hidden="true" />
                </>
              )}
            </button>
          </form>

          {/* Demo credentials box */}
          {mode === 'login' && (
            <div className="mt-6 p-4 rounded-lg border border-border bg-muted/40">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Demo-Zugangsdaten</p>
                <button
                  onClick={autofill}
                  className="text-xs text-primary hover:underline font-medium"
                >
                  Automatisch einfügen
                </button>
              </div>
              <div className="space-y-2">
                {([
                  ['E-Mail', DEMO_CREDENTIALS.email, 'email'],
                  ['Passwort', DEMO_CREDENTIALS.password, 'password'],
                ] as [string, string, 'email' | 'password'][]).map(([label, value, field]) => (
                  <div key={`cred-${field}`} className="flex items-center justify-between gap-2">
                    <span className="text-xs text-muted-foreground w-16 flex-shrink-0">{label}:</span>
                    <span className="font-mono text-xs text-foreground flex-1 truncate">{value}</span>
                    <button
                      onClick={() => copyToClipboard(field)}
                      className="text-muted-foreground hover:text-primary transition-colors flex-shrink-0"
                      title={`${label} kopieren`}
                    >
                      {copiedField === field ? <Check size={13} className="text-success" /> : <Copy size={13} />}
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-1.5 text-xs text-amber-600">
                <Clock size={11} />
                <span>Demo-Sitzung: automatischer Logout nach 60 Min.</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}